import { useQuery } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import axios from "../api/axios";

export interface PublicOfficine {
    _id: string;
    name: string;
    phone: string;
    street: string;
    district: string;
    city: string;
}

export interface PublicGarde {
    periodStart: string;
    periodEnd: string;
    groupNumber: number;
    isOverride: boolean;
    officines: PublicOfficine[];
    peripheryReinforcement: PublicOfficine | null;
}

export const useCurrentGarde = () => {
    return useQuery({
        queryKey: ["public", "garde", "current"],
        queryFn: async () => {
            const { data } = await axios.get<{
                success: boolean;
                garde: PublicGarde;
            }>("/gardes/current");
            return data.garde;
        },
        staleTime: 1000 * 60 * 10,
    });
};

export interface NearbyOfficine {
    _id: string;
    name: string;
    phone: string;
    street: string;
    district: string;
    city: string;
    groupNumber: number;
    isPeriphery: boolean;
    coordinates: [number, number];
    distanceMeters: number;
}

type GeolocationState =
    | { status: "idle" }
    | { status: "locating" }
    | { status: "error"; message: string }
    | { status: "success"; lat: number; lng: number };

export const useNearbyOfficines = () => {
    const [geoState, setGeoState] = useState<GeolocationState>({
        status: "idle",
    });

    const {
        data: officines,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: ["public", "officines", "nearby", geoState],
        queryFn: async () => {
            if (geoState.status !== "success") return [];
            const { data } = await axios.get<{ officines: NearbyOfficine[] }>(
                "/officines/nearby",
                { params: { lat: geoState.lat, lng: geoState.lng } },
            );
            return data.officines;
        },
        enabled: geoState.status === "success",
    });

    const locate = useCallback(() => {
        if (!navigator.geolocation) {
            setGeoState({
                status: "error",
                message:
                    "La géolocalisation n'est pas supportée par ce navigateur.",
            });
            return;
        }

        setGeoState({ status: "locating" });

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setGeoState({
                    status: "success",
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                });
            },
            (error) => {
                const message =
                    error.code === error.PERMISSION_DENIED
                        ? "Autorisation de localisation refusée. Active-la dans les paramètres de ton navigateur."
                        : "Impossible de déterminer ta position.";
                setGeoState({ status: "error", message });
            },
            { enableHighAccuracy: true, timeout: 10000 },
        );
    }, []);

    return {
        locate,
        geoState,
        officines: officines ?? [],
        isLoading: geoState.status === "locating" || isFetching,
        refetch,
    };
};
